import React, { useState, useRef, useEffect, useCallback } from "react";
import { PreviousUpload, Upload, UploadOptions } from "@maysoft/tus-js-client";
import {
    Tooltip,
    FormLabel,
    IconButton,
    FormControl,
    FormHelperText,
    LinearProgress,
} from "@mui/material";
import {
    Clear as ClearIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
    Upload as UploadIcon
} from "@mui/icons-material";

import "./styles.css";
import nanoid from "../../lib/nanoid";
import VideoPlayer from "../VideoPlayer";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import Resources from "../../constants/resources";
import ResumableUploadService, { ResumableUploadRequest } from "../../services/asset/resumableUpload.service";

import { Method } from "../../services/base/HttpClient";
import { UploadFileResponse } from "../../commons/interfaces";
import { FileAccessMode, FileProcessingStatus, Mode } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";

interface IProps {
    mode?: Mode;
    label: string;
    fileId?: string;
    folderPath?: string;
    className?: string;
    required?: boolean;
    limitFileSize?: number;
    errorMessage?: string;
    optimize?: boolean;
    accessMode?: FileAccessMode;
    onChangeVideo: (res?: UploadFileResponse) => void;
}

interface ExtendedPreviousUpload extends PreviousUpload {
    uploadUrl: string;
    urlStorageKey: string;
    parallelUploadUrls?: string[];
}

const VideoUploader: React.FC<IProps> = (props: IProps) => {
    const strings = useStrings();
    const videoUploadInputId = nanoid();
    const videoUploadLabelId = nanoid();

    const context = useCommonComponentContext();

    const tusUpload = useRef<Upload>();
    const tusUploadUrl = useRef<string>();
    const videoInputRef = useRef<any>(null);

    const [fileId, setFileId] = useState<string>();
    const [pausing, setPausing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState(props.errorMessage || "");

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(props.fileId)) {
            setFileId(props.fileId);
        }
        return () => {
            terminateUpload();
        }
    }, [props.fileId]);

    const uploadFile = async (file: File) => {
        let validFile = true;
        setUploading(false);
        setUploadProgress(0);
        setErrorMessage("");

        if (Helpers.isNumber(props.limitFileSize) && file.size > props.limitFileSize) {
            validFile = false;
            setErrorMessage(strings.format(strings.Common.FILE_SIZE_MUST_SMALLER_THAN, Helpers.bytesToSize(props.limitFileSize)) as string);
        }

        // if (Helpers.getFileExtesion(file.name) !== ".mp4") {
        //     validFile = false;
        //     setErrorMessage(strings.Message.VIDEO_MUST_BE_MP4_FORMAT);
        // }

        if (!validFile) {
            videoInputRef.current.value = null;
            props.onChangeVideo(undefined);
            return;
        }

        try {
            const parallelUploads = 6;

            const processingStatus = (props.optimize === false)
                ? `${FileProcessingStatus.Processed}`
                : `${FileProcessingStatus.Unprocessed}`;

            const accessMode = !Helpers.isNullOrEmpty(props.accessMode)
                ? `${props.accessMode}`
                : `${FileAccessMode.Private}`;

            const request: ResumableUploadRequest = {
                accessMode: accessMode,
                processingStatus: processingStatus,
                tenantCode: context.tenantCode,
                serviceCode: context.serviceCode
            };

            if (!Helpers.isNullOrEmpty(props?.folderPath)) {
                request.folderPath = props.folderPath;
            }

            const options: UploadOptions = {
                parallelUploads,
                onError: (error) => {
                    handleUploadError();
                    console.log("Uploading failed: ", error);
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = parseInt((bytesUploaded / bytesTotal * 100).toFixed(2));
                    setUploadProgress(percentage);
                },
                onBeforeRequest: (req) => {
                    if (Helpers.isNullOrEmpty(tusUploadUrl.current) &&
                        req.getMethod() === Method.PATCH
                    ) {
                        tusUploadUrl.current = req.getURL();
                    }
                },
                onSuccess: (res) => {
                    let id = "";
                    if (res.getHeader("asset-id")) {
                        id = res.getHeader("asset-id");
                    }

                    let accessUrl = "";
                    if (res.getHeader("asset-url")) {
                        accessUrl = res.getHeader("asset-url");
                    }

                    let error = "";
                    if (res.getHeader("asset-error")) {
                        error = res.getHeader("asset-error");
                        setErrorMessage(error);
                        handleUploadError();
                    }

                    const response = {
                        id,
                        error,
                        accessUrl,
                    }

                    tusUpload.current = undefined;
                    tusUploadUrl.current = undefined;
                    props.onChangeVideo(response);
                    setFileId(response.id);
                    setUploading(false);
                }
            };

            tusUpload.current = ResumableUploadService.upload(file, request, options);

            setUploading(true);
            setPausing(false);
            tusUpload.current.start();

        } catch (error) {
            if (tusUpload.current) {
                tusUpload.current.abort();
            }
            handleUploadError();
        }
    }

    const resumeUpload = (tusUpload: Upload) => {
        tusUpload.findPreviousUploads().then((allPreviousUploads) => {
            for (let index = 0; index < allPreviousUploads.length; index++) {
                const previousUpload = allPreviousUploads[index] as ExtendedPreviousUpload;

                if (
                    tusUploadUrl.current &&
                    Array.isArray(previousUpload.parallelUploadUrls) &&
                    previousUpload.parallelUploadUrls.indexOf(tusUploadUrl.current) > -1
                ) {
                    tusUpload.resumeFromPreviousUpload(allPreviousUploads[index]);
                    tusUpload.start();
                    break;
                }
            }
        }).catch((error) => {
            console.log("Error when resume upload:", error);
        })
    }

    const terminateUpload = () => {
        // Abort upload first, then remove upload key in localStorage
        if (tusUpload.current) {
            tusUpload.current.abort();
            tusUpload.current.findPreviousUploads().then((allPreviousUploads) => {
                for (let index = 0; index < allPreviousUploads.length; index++) {
                    const previousUpload = allPreviousUploads[index] as ExtendedPreviousUpload;
                    if (
                        tusUploadUrl.current &&
                        Array.isArray(previousUpload.parallelUploadUrls) &&
                        previousUpload.parallelUploadUrls.indexOf(tusUploadUrl.current) > -1
                    ) {
                        localStorage.removeItem(previousUpload.urlStorageKey);
                        previousUpload.parallelUploadUrls?.forEach((url: string) => {
                            Upload.terminate(url).catch((error) => {
                                console.log("Error when terminate upload:", error);
                            })
                        })
                        break;
                    }
                }
            }).catch((error) => {
                // console.log("Error when abort upload:", error);
            }).finally(() => {
                tusUploadUrl.current = undefined;
            })
        }
    }

    const handleContinueOrPauseUpload = useCallback(() => {
        if (tusUpload.current && uploadProgress < 100) {
            if (pausing) {
                setPausing(false);
                resumeUpload(tusUpload.current);
            } else {
                setPausing(true);
                tusUpload.current.abort();
            }
        }
    }, [pausing, uploadProgress])

    const handleTerminateUpload = useCallback(async () => {
        if (uploadProgress < 100) {
            setPausing(true);
            tusUpload.current && tusUpload.current.abort();
            Helpers.showConfirmAlert(strings.Common.CANCEL_UPLOAD_VIDEO,
                () => {
                    terminateUpload();
                    videoInputRef.current.value = null;
                    props.onChangeVideo(undefined);
                    setFileId(undefined);
                    setUploading(false);
                },
                () => {
                    setPausing(false);
                    tusUpload.current && resumeUpload(tusUpload.current);
                },
                strings.Common.YES,
                strings.Common.NO
            );
        }
    }, [uploadProgress, strings])

    const handleUploadError = () => {
        if (videoInputRef.current) {
            videoInputRef.current.value = null;
            setUploading(false);
            setUploadProgress(0);
            setErrorMessage(strings.Common.UPLOAD_VIDEO_FAILED);
        }
        props.onChangeVideo(undefined);
    }

    const handleReplaceVideo = () => {
        setFileId(undefined);
        videoInputRef.current.value = null;
        videoInputRef.current.click();
    }

    return (
        <FormControl
            className={`${props.className} video-uploader-root`}
            error={!Helpers.isNullOrEmpty(props.errorMessage) || !Helpers.isNullOrEmpty(errorMessage)}>
            <FormLabel style={{ marginBottom: 8, fontSize: "0.875rem", fontWeight: "400" }}>
                {props.required ? `${props.label} *` : props.label}
            </FormLabel>

            <div className={"d-flex flex-column justify-content-center align-items-center video-uploader-container"}>
                <div className={"player-wrapper video-uploader-pickerWrapper"}>
                    {fileId ? (
                        <VideoPlayer fileId={fileId} />
                    ) : (
                        uploading ? (
                            <div className="react-player px-2 w-100 h-100 bg-grey-100 d-flex flex-column justify-content-center p-1 align-items-center">
                                <div className="w-100 d-flex justify-content-center align-items-center">
                                    <LinearProgress
                                        className="mr-1"
                                        variant="determinate"
                                        style={{ width: "80%", height: 7 }}
                                        value={uploadProgress}
                                    />

                                    <div style={{ width: "20%" }}>
                                        {(props.mode !== Mode.View) && (
                                            <Tooltip
                                                arrow
                                                title={strings.Common.CANCEL}
                                                aria-label={strings.Common.CANCEL}
                                                placement="top">
                                                <IconButton size="small" onClick={handleTerminateUpload}>
                                                    <ClearIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        <Tooltip
                                            arrow
                                            title={pausing ? strings.Common.CONTINUE : strings.Common.PAUSE}
                                            aria-label={pausing ? strings.Common.CONTINUE : strings.Common.PAUSE}
                                            placement="top">
                                            <IconButton size="small" onClick={handleContinueOrPauseUpload}>
                                                {pausing ? (
                                                    <PlayArrowIcon />
                                                ) : (
                                                    <PauseIcon />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </div>

                                <div className="w-100">
                                    {`${strings.Common.STATUS}: ${uploadProgress}%`}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <img
                                    width={65}
                                    alt="thumbnail"
                                    src={Resources.IMAGES.VIDEO_FILES}
                                />
                                {props?.mode !== Mode.View && (
                                    <label
                                        id={videoUploadLabelId}
                                        htmlFor={videoUploadInputId}
                                        style={{ cursor: "pointer" }}
                                        className={"d-flex align-items-center mt-3 video-uploader-uploadButton"}>
                                        <UploadIcon sx={{ mr: 1 }} fontSize="small" />
                                        {strings.Common.UPLOAD_VIDEO}
                                    </label>
                                )}
                            </div>
                        )
                    )}
                </div>
                {(!Helpers.isNullOrEmpty(fileId) && props?.mode !== Mode.View) && (
                    <div className="d-flex justify-content-end align-items-center w-100 pr-4 pt-3">
                        <div
                            onClick={handleReplaceVideo}
                            className={"d-flex align-items-center video-uploader-uploadButton"}>
                            <UploadIcon sx={{ mr: 1 }} fontSize="small" />
                            {strings.Common.REPLACE}
                        </div>
                    </div>
                )}

                {/* {videoInfo?.processingStatus === FileProcessingStatus.Processing && (
                        <div className="mt-2" style={{ fontWeight: "bold", color: Constants.Styles.PRIMARY_COLOR }}>
                            {strings.Common.ATTENTION}{`: ${strings.Message.LESSON_VIDEO_PROCESSING}`}
                        </div>
                    )} */}
            </div>

            <input
                hidden
                ref={videoInputRef}
                id={videoUploadInputId}
                type="file"
                accept="video/*"
                onChange={(event) => {
                    if (event.target.files?.[0]) {
                        uploadFile(event.target.files[0]);
                    }
                }}
            />

            <FormHelperText>
                {props.errorMessage || errorMessage}
            </FormHelperText>
        </FormControl>
    );
}

export default VideoUploader;
